import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Lock, Eye, EyeSlash, CheckCircle, Warning } from '@phosphor-icons/react';
import api from '../services/api';

const PASSWORD_RULES = [
  { test: (p) => p.length >= 8, label: 'At least 8 characters' },
  { test: (p) => /[A-Z]/.test(p), label: 'One uppercase letter' },
  { test: (p) => /[a-z]/.test(p), label: 'One lowercase letter' },
  { test: (p) => /\d/.test(p), label: 'One number' },
];

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [emailMasked, setEmailMasked] = useState('');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setTokenError('This reset link is missing its security token. Please request a new link.');
      return;
    }
    api
      .post('/auth/verify-reset-token', { token })
      .then((resp) => {
        setTokenValid(true);
        setEmailMasked(resp.data?.email_masked || '');
      })
      .catch((err) => {
        setTokenError(err.response?.data?.detail || 'This reset link is invalid or has expired.');
      })
      .finally(() => setVerifying(false));
  }, [token]);

  const rulesMet = PASSWORD_RULES.map((r) => r.test(password));
  const allRulesMet = rulesMet.every(Boolean);
  const passwordsMatch = password && password === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allRulesMet) {
      toast.error('Password does not meet the requirements.');
      return;
    }
    if (!passwordsMatch) {
      toast.error('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setSuccess(true);
      toast.success('Password updated!');
      setTimeout(() => navigate('/login'), 1800);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Reset failed. Please request a new link.';
      toast.error(typeof detail === 'string' ? detail : 'Reset failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (verifying) {
      return (
        <div className="py-10 text-center" data-testid="reset-verifying">
          <div className="w-10 h-10 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-3 text-sm text-slate-500">Verifying your reset link…</p>
        </div>
      );
    }
    if (!tokenValid) {
      return (
        <div className="py-6 text-center" data-testid="reset-token-invalid">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
            <Warning size={28} className="text-amber-600" weight="fill" />
          </div>
          <h3 className="text-base font-semibold text-slate-900 mb-1">Reset link problem</h3>
          <p className="text-sm text-slate-600 mb-5 leading-relaxed">{tokenError}</p>
          <Link to="/forgot-password">
            <Button className="bg-indigo-900 hover:bg-indigo-800" data-testid="request-new-link-btn">
              Request a new link
            </Button>
          </Link>
        </div>
      );
    }
    if (success) {
      return (
        <div className="py-8 text-center" data-testid="reset-success">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
            <CheckCircle size={28} className="text-emerald-600" weight="fill" />
          </div>
          <h3 className="text-base font-semibold text-slate-900 mb-1">Password updated</h3>
          <p className="text-sm text-slate-600 mb-5">Taking you to sign in…</p>
        </div>
      );
    }
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {emailMasked && (
          <p className="text-xs text-slate-500 text-center pb-1">
            Resetting password for <span className="font-medium text-slate-700">{emailMasked}</span>
          </p>
        )}

        <div>
          <Label htmlFor="password" className="text-slate-700">New password</Label>
          <div className="relative mt-1">
            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 pr-10"
              placeholder="••••••••"
              autoFocus
              required
              autoComplete="new-password"
              data-testid="reset-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              tabIndex={-1}
            >
              {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div>
          <Label htmlFor="confirm" className="text-slate-700">Confirm new password</Label>
          <Input
            id="confirm"
            type={showPassword ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="mt-1"
            placeholder="••••••••"
            required
            autoComplete="new-password"
            data-testid="reset-confirm-input"
          />
          {confirm && !passwordsMatch && (
            <p className="text-xs text-red-600 mt-1">Passwords don't match</p>
          )}
        </div>

        <ul className="text-xs space-y-1 pt-1 pb-1">
          {PASSWORD_RULES.map((rule, i) => (
            <li
              key={rule.label}
              className={`flex items-center gap-1.5 ${rulesMet[i] ? 'text-emerald-600' : 'text-slate-400'}`}
            >
              <CheckCircle size={12} weight={rulesMet[i] ? 'fill' : 'regular'} />
              {rule.label}
            </li>
          ))}
        </ul>

        <Button
          type="submit"
          disabled={submitting || !allRulesMet || !passwordsMatch}
          className="w-full bg-indigo-900 hover:bg-indigo-800 h-11"
          data-testid="reset-submit-btn"
        >
          {submitting ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            'Update password'
          )}
        </Button>
      </form>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"
          data-testid="reset-back-to-login"
        >
          <ArrowLeft size={16} /> Back to sign in
        </Link>
        <Card className="shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                <Lock size={22} className="text-indigo-700" weight="regular" />
              </div>
              <div>
                <CardTitle className="text-xl" data-testid="reset-password-title">Choose a new password</CardTitle>
                <CardDescription className="text-sm">
                  Pick a strong password you haven't used before.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>{renderBody()}</CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
